import React from 'react';

import i18n from 'util/i18n';
import { Summary, DateSummary } from 'components/Summary';

import { LEGAL, LEGAL_INVESTIGATIONS_PIV_ISSUES } from 'config/formSections/legal';

import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import { EmptyListInstance } from 'forms/shared/models';
import Branch from 'components/Form/Branch';
import Accordion from 'components/Form/Accordion';
import SuspendedItem from './SuspendedItem';
import RevokedItem from './RevokedItem';
import DeniedItem from './DeniedItem';

const sectionConfig = {
  key: LEGAL_INVESTIGATIONS_PIV_ISSUES.key,
  section: LEGAL.name,
  store: LEGAL.store,
  subsection: LEGAL_INVESTIGATIONS_PIV_ISSUES.name,
  storeKey: LEGAL_INVESTIGATIONS_PIV_ISSUES.storeKey,
};

export class PivIssues extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
  }

  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      List: this.props.List, // For revocations. Left as List to support legacy form types.
      HasRevocations: this.props.HasRevocations,
      DenialList: this.props.DenialList,
      HasDenials: this.props.HasDenials,
      SuspensionList: this.props.SuspensionList,
      HasSuspensions: this.props.HasSuspensions,
      ...queue,
    });
  };

  updateRevocationList = (values) => {
    this.update({
      List: values,
    });
  };

  updateRevocationsBranch = (values) => {
    this.update({
      HasRevocations: values,
      List: values.value === 'Yes' ? this.props.List : [],
    });
  };

  RevocationSummary = (item, index) => {
    const o = (item && item.Item) || {};
    const dates = DateSummary(o.Date);
    const agency = (o.Agency || {}).value || '';

    return Summary({
      type: i18n.t('legal.pvq.investigations.pivissues.revoked.collection.item'),
      index,
      left: agency,
      right: dates,
      placeholder: i18n.t('legal.pvq.investigations.pivissues.revoked.collection.unknown'),
    });
  };

  updateDenialList = (values) => {
    this.update({
      DenialList: values,
    });
  };

  updateDenialsBranch = (values) => {
    this.update({
      HasDenials: values,
      DenialList: values.value === 'Yes' ? this.props.DenialList : [],
    });
  };

  DenialSummary = (item, index) => {
    const o = (item && item.Item) || {};
    const dates = DateSummary(o.Date);
    const agency = (o.Agency || {}).value || '';

    return Summary({
      type: i18n.t('legal.pvq.investigations.pivissues.denied.collection.item'),
      index,
      left: agency,
      right: dates,
      placeholder: i18n.t('legal.pvq.investigations.pivissues.denied.collection.unknown'),
    });
  };

  updateSuspensionList = (values) => {
    this.update({
      SuspensionList: values,
    });
  };

  updateSuspensionBranch = (values) => {
    this.update({
      HasSuspensions: values,
      SuspensionList: values.value === 'Yes' ? this.props.SuspensionsList : [],
    });
  };

  SuspensionSummary = (item, index) => {
    const o = (item && item.Item) || {};
    const dates = DateSummary(o.Date);
    const agency = (o.Agency || {}).value || '';

    return Summary({
      type: i18n.t('legal.pvq.investigations.pivissues.suspended.collection.item'),
      index,
      left: agency,
      right: dates,
      placeholder: i18n.t('legal.pvq.investigations.pivissues.suspended.collection.unknown'),
    });
  };

  render() {
    const { errors } = this.props;
    const suspensionAccordionErrors = errors && errors.filter((e) => e.indexOf('SuspensionList.accordion') === 0);
    const revocationAccordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const denialAccordionErrors = errors && errors.filter((e) => e.indexOf('DenialList.accordion') === 0);
    const prefixPath = 'legal.pvq.investigations.pivissues';

    return (
      <div
        className="section-content investigations-pivissues"
        data-section={LEGAL.key}
        data-subsection={LEGAL_INVESTIGATIONS_PIV_ISSUES.key}
      >
        <h1 className="section-header">{i18n.t('legal.pvq.subsection.investigations.pivissues')}</h1>

        <Branch
          {...this.props.HasSuspensions}
          name="has_pivsuspensions"
          label={i18n.t('legal.pvq.investigations.pivissues.suspended.heading.title')}
          labelSize="h4"
          className="legal-investigations-pivissues-has-suspensions"
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateSuspensionBranch}
          scrollIntoView={this.props.scrollIntoView}
          attribute="HasSuspensions"
          showDNK
        />

        {this.props.HasSuspensions.value === 'Yes'
          && (
          <Accordion
            {...this.props.SuspensionList}
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.SuspensionSummary}
            onUpdate={this.updateSuspensionList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={suspensionAccordionErrors}
            description={i18n.t('legal.pvq.investigations.pivissues.suspended.collection.description')}
            appendTitle={i18n.t('legal.pvq.investigations.pivissues.suspended.collection.appendTitle')}
            appendLabel={i18n.t('legal.pvq.investigations.pivissues.suspended.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute="SuspensionList"
          >
            <SuspendedItem
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute="PivSuspendedList"
              formType={this.props.formType}
              prefix={prefixPath}
              showReinstatedDateWhenReinstated
            />
          </Accordion>
          )}

        <Branch
          {...this.props.HasRevocations}
          name="has_pivrevocations"
          label={i18n.t('legal.pvq.investigations.pivissues.revoked.heading.title')}
          labelSize="h4"
          className="legal-investigations-pivissues-has-revocations"
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateRevocationsBranch}
          scrollIntoView={this.props.scrollIntoView}
          attribute="HasRevocations"

        />

        {this.props.HasRevocations.value === 'Yes'
          && (
          <Accordion
            {...this.props.List}
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.RevocationSummary}
            onUpdate={this.updateRevocationList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={revocationAccordionErrors}
            description={i18n.t('legal.pvq.investigations.pivissues.revoked.collection.description')}
            appendTitle={i18n.t('legal.pvq.investigations.pivissues.revoked.collection.appendTitle')}
            appendLabel={i18n.t('legal.pvq.investigations.pivissues.revoked.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute="List"
            addendumExactAttribute
          >
            <RevokedItem
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute="PivRevokedList"
              formType={this.props.formType}
              prefix={prefixPath}
            />
          </Accordion>
          )}

        <Branch
          {...this.props.HasDenials}
          name="has_pivdenials"
          label={i18n.t('legal.pvq.investigations.pivissues.denied.heading.title')}
          labelSize="h4"
          className="legal-investigations-pivissues-has-denials"
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateDenialsBranch}
          scrollIntoView={this.props.scrollIntoView}
          attribute="HasDenials"

        />

        {this.props.HasDenials.value === 'Yes'
          && (
          <Accordion
            {...this.props.DenialList}
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.DenialSummary}
            onUpdate={this.updateDenialList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={denialAccordionErrors}
            description={i18n.t('legal.pvq.investigations.pivissues.denied.collection.description')}
            appendTitle={i18n.t('legal.pvq.investigations.pivissues.denied.collection.appendTitle')}
            appendLabel={i18n.t('legal.pvq.investigations.pivissues.denied.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute="DenialList"
          >
            <DeniedItem
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute="List"
              formType={this.props.formType}
              prefix={prefixPath}
            />
          </Accordion>
          )}
      </div>
    );
  }
}

PivIssues.defaultProps = {
  name: 'pivissues',
  HasRevocations: {},
  List: EmptyListInstance,
  HasDenials: {},
  DenialList: EmptyListInstance,
  HasSuspensions: {},
  SuspensionsList: EmptyListInstance,
  defaultState: true,
  onUpdate: () => { },
  onError: (value, arr) => arr,
  section: 'legal',
  subsection: 'investigations/pivissues',
  dispatch: () => { },
  scrollToBottom: '',
  errors: [],
};

export default connectSubsection(PivIssues, sectionConfig);
